"use client";

import { useCallback, useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { Send, Pin } from "lucide-react";
import { EmptyState } from "./EmptyState";
import { ErrorBanner } from "./ErrorBanner";
import { MessageList } from "./MessageList";
import { NewChatButton } from "./NewChatButton";
import { PinIndicator } from "./PinIndicator";
import { SettingsPanel } from "./SettingsPanel";
import { SignOutConfirmDialog } from "./SignOutConfirmDialog";
import { Toast, type ToastVariant } from "./Toast";
import type { ChatMessage } from "./MessageBubble";
import {
  sendMessage,
  startSession,
  ApiError,
  humanizeApiError,
  isPersistentError,
  type CallerMetadata,
} from "@/lib/api";
import { getSessionId, setSessionId, clearSessionId } from "@/lib/session";
import { getPin, setPin, clearPin, type PinSnapshot } from "@/lib/pin";
import { readSelection, NoWorkbookError, NoSelectionError } from "@/lib/office";
import { signOut } from "@/lib/auth";
import { resolveTenant } from "@/lib/tenant";
import { track, captureError } from "@/lib/telemetry";

const CLIENT_VERSION = "0.1.0";
const NUDGE_THRESHOLD = 3;

export function ChatPanel({
  mirrorId,
  onSignOut,
  onSwitchWorkspace,
}: {
  mirrorId: string;
  onSignOut: () => void;
  onSwitchWorkspace: () => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isWaiting, setIsWaiting] = useState(false);
  const [toast, setToast] = useState<{ variant: ToastVariant; message: string } | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [selectionWarning, setSelectionWarning] = useState<string | null>(null);
  const [sessionId, setSessionIdState] = useState<string | null>(getSessionId);
  const [pin, setPinState] = useState<PinSnapshot | null>(() => {
    const sid = getSessionId();
    return sid ? getPin(sid) : null;
  });
  const [_recentAddresses, setRecentAddresses] = useState<string[]>([]);
  const [showNudge, setShowNudge] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // When sessionId changes, refresh pin from storage
  useEffect(() => {
    if (sessionId) {
      setPinState(getPin(sessionId));
    } else {
      setPinState(null);
    }
  }, [sessionId]);

  const handleNewChat = useCallback(() => {
    if (sessionId) clearPin(sessionId);
    clearSessionId();
    setSessionIdState(null);
    setMessages([]);
    setToast(null);
    setPinState(null);
    setRecentAddresses([]);
    setShowNudge(false);
  }, [sessionId]);

  const handleSignOut = useCallback(() => {
    if (pin && !showSignOutConfirm) {
      setShowSignOutConfirm(true);
      return;
    }
    signOut(sessionId);
    setShowSignOutConfirm(false);
    onSignOut();
  }, [pin, sessionId, showSignOutConfirm, onSignOut]);

  const handlePinToggle = useCallback(async () => {
    if (!sessionId) {
      setToast({ variant: "info", message: "Send a message first to start a session." });
      return;
    }
    if (pin) {
      // Currently pinned → clear
      clearPin(sessionId);
      setPinState(null);
      track("excel.pin_cleared", { session_id: sessionId });
      return;
    }
    try {
      const sel = await readSelection();
      if (sel.rowCount === 1 && sel.columnCount === 1 && sel.values[0]?.[0] === "") {
        setToast({
          variant: "info",
          message: "That cell is empty. Select actual data before pinning.",
        });
        return;
      }
      const snap: PinSnapshot = { ...sel, pinnedAt: Date.now() };
      setPin(sessionId, snap);
      setPinState(snap);
      setShowNudge(false);
      track("excel.pin_set", { range_size: sel.rowCount * sel.columnCount });
    } catch (e) {
      captureError(e, { where: "ChatPanel.pin" });
      if (e instanceof NoWorkbookError) {
        setToast({
          variant: "error",
          message: "No workbook open — open a spreadsheet first, then pin a range.",
        });
      } else if (e instanceof NoSelectionError) {
        setToast({
          variant: "error",
          message: "Nothing to pin — select a range with actual data, then click Pin.",
        });
      } else {
        setToast({
          variant: "error",
          message: "Couldn't read the selection. Make sure you have cells selected.",
        });
      }
    }
  }, [sessionId, pin]);

  const handleAcceptNudge = useCallback(() => {
    setShowNudge(false);
    handlePinToggle();
  }, [handlePinToggle]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const text = input.trim();
      if (!text || isWaiting) return;

      setInput("");
      setErrorBanner(null);

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        text,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsWaiting(true);

      try {
        let sid = sessionId;
        if (!sid) {
          const s = await startSession();
          sid = s.session_id;
          setSessionId(sid);
          setSessionIdState(sid);
          track("excel.session_started", { session_id: sid });
        }

        let selection: CallerMetadata["selection"] | undefined;
        try {
          selection = await readSelection();
          setSelectionWarning(null);
        } catch (e) {
          if (e instanceof NoWorkbookError) {
            setSelectionWarning(
              "No workbook open — open a spreadsheet to give the agent context.",
            );
          } else if (e instanceof NoSelectionError) {
            setSelectionWarning(
              "No cells selected — click a cell or range for context.",
            );
          }
          // Continue with send (no selection context)
        }

        // Track recent selection addresses for nudge logic
        if (selection?.address) {
          setRecentAddresses((prev) => {
            const next = [...prev, selection.address].slice(-NUDGE_THRESHOLD);
            // If pin not set and last N addresses overlap (string-prefix match), trigger nudge.
            if (!pin && next.length === NUDGE_THRESHOLD) {
              const allOverlap = next.slice(1).every((a) =>
                shareSheetAndOverlap(a, next[0]),
              );
              if (allOverlap) setShowNudge(true);
            }
            return next;
          });
        }

        const currentPin = sid ? getPin(sid) : null;

        track("excel.message_sent", {
          session_id: sid,
          selection_size: selection ? selection.rowCount * selection.columnCount : 0,
          workbook_name: selection?.workbookName,
          pinned: currentPin != null,
        });

        const start = Date.now();
        const result = await sendMessage(sid, {
          text,
          caller_metadata: {
            client: "excel-addin",
            client_version: CLIENT_VERSION,
            workbook_name: selection?.workbookName,
            sheet_name: selection?.sheetName,
            selection,
            pin: currentPin ?? undefined,
          },
        });

        const latencyMs = Date.now() - start;
        track("excel.message_received", {
          session_id: sid,
          latency_ms: latencyMs,
          // action_count populated by ActionMessage on render
        });

        const replyText =
          result.response?.text ?? result.response_error ?? "(no response)";
        setMessages((prev) => [
          ...prev,
          {
            id: result.message_id,
            role: "assistant",
            text: replyText,
            timestamp: Date.now(),
          },
        ]);
      } catch (e) {
        captureError(e, { where: "ChatPanel.send" });
        const message = humanizeApiError(e);
        if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
          clearSessionId();
          setSessionIdState(null);
          setToast({ variant: "error", message });
        } else if (isPersistentError(e)) {
          setErrorBanner(message);
        } else {
          setToast({ variant: "error", message });
        }
      } finally {
        setIsWaiting(false);
      }
    },
    [input, isWaiting, sessionId, pin],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void handleSubmit(e as unknown as FormEvent);
      }
    },
    [handleSubmit],
  );

  void mirrorId;

  return (
    <div className="flex h-full flex-col bg-[var(--surface-base)]">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-default)] px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-[var(--text-secondary)]">myai</span>
          {pin && <PinIndicator pin={pin} onClear={handlePinToggle} />}
        </div>
        <div className="flex items-center gap-2">
          {!pin && (
            <button
              onClick={handlePinToggle}
              aria-label="Pin current selection"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--hover-overlay)] hover:text-[var(--text-primary)]"
            >
              <Pin size={14} strokeWidth={1.5} />
              Pin
            </button>
          )}
          <NewChatButton onClick={handleNewChat} />
          <SettingsPanel
            tenant={resolveTenant() ?? ""}
            onSignOut={handleSignOut}
            onSwitchWorkspace={onSwitchWorkspace}
          />
        </div>
      </header>

      {errorBanner && (
        <ErrorBanner
          message={errorBanner}
          sessionId={sessionId}
          context="send_message"
          onDismiss={() => setErrorBanner(null)}
        />
      )}

      {showNudge && (
        <div className="border-b border-[var(--border-default)] bg-[var(--surface-raised)] px-3 py-2 text-[13px]">
          <span className="text-[var(--text-secondary)]">
            Looks like you're working on the same range — pin this as your dataset?{" "}
          </span>
          <button
            onClick={handleAcceptNudge}
            className="text-[var(--brand-accent)] hover:underline"
          >
            Pin it
          </button>
          <button
            onClick={() => setShowNudge(false)}
            className="ml-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <MessageList
          messages={messages}
          isWaiting={isWaiting}
          sessionId={sessionId}
          emptyState={<EmptyState onSelect={(prompt) => setInput(prompt)} />}
        />
      </div>

      {selectionWarning && (
        <div className="flex items-center gap-2 border-t border-[var(--border-subtle)] px-3 py-1.5 text-[12px] text-[var(--text-muted)]">
          <span>{selectionWarning}</span>
          <button
            onClick={() => setSelectionWarning(null)}
            className="ml-auto text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-2 border-t border-[var(--border-default)] p-3"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about the selected cells…"
          aria-label="Message input"
          rows={1}
          className="min-h-[44px] max-h-[120px] flex-1 resize-none rounded-xl border border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-accent)] focus:outline-none"
          disabled={isWaiting}
        />
        <button
          type="submit"
          aria-label="Send message"
          disabled={!input.trim() || isWaiting}
          className="rounded-md p-2 text-[var(--text-muted)] hover:bg-[var(--hover-overlay)] disabled:cursor-not-allowed enabled:text-[var(--brand-accent)]"
        >
          <Send size={20} strokeWidth={1.5} />
        </button>
      </form>

      {toast && (
        <Toast
          variant={toast.variant}
          message={toast.message}
          onDismiss={() => setToast(null)}
        />
      )}

      <SignOutConfirmDialog
        open={showSignOutConfirm}
        onCancel={() => setShowSignOutConfirm(false)}
        onConfirm={handleSignOut}
      />
    </div>
  );
}

/**
 * Two range addresses overlap if they share the sheet prefix and any cell range overlaps.
 * For the nudge heuristic we only need a cheap approximate check — string-prefix match on
 * "SheetName!FirstColumn" works for most analyst patterns (D2:D101 vs D2:D200 vs D5:D80).
 */
function shareSheetAndOverlap(a: string, b: string): boolean {
  const sheetA = a.split("!")[0];
  const sheetB = b.split("!")[0];
  if (sheetA !== sheetB) return false;
  // Crude: share first 3 characters of the range portion (e.g., "D2:" matches "D2:" or "D5:")
  const rangeA = a.split("!")[1] ?? "";
  const rangeB = b.split("!")[1] ?? "";
  const colA = rangeA.match(/^[A-Z]+/i)?.[0];
  const colB = rangeB.match(/^[A-Z]+/i)?.[0];
  return colA != null && colA === colB;
}
